import { cn } from "@fieldwork/ui/lib/utils";
import { Link, useRouterState } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { authClient } from "@/lib/auth-client";
import { useAgentQuery } from "@/lib/act";
import { useOpenPlane } from "@/lib/plane";

import { useServer } from "./server-hooks";

/** The Recursion mark (nested squares, orange core), as on infinitumlabs.studio. */
export function Logo({ size = 26 }: { size?: number }) {
  return (
    <svg viewBox="-54 -54 108 108" width={size} height={size} aria-hidden="true">
      <g fill="none" stroke="currentColor" strokeWidth="7" strokeLinejoin="round">
        <polygon points="50,50 -50,50 -50,-50 50,-50" />
        <polygon points="28.87,50 -50,28.87 -28.87,-50 50,-28.87" />
        <polygon points="12.2,45.53 -45.53,12.2 -12.2,-45.53 45.53,-12.2" />
        <polygon points="0,38.49 -38.49,0 0,-38.49 38.49,0" />
      </g>
      <polygon points="-14.81,8.55 -8.55,-14.81 14.81,-8.55 8.55,14.81" fill="#FF4F1A" />
    </svg>
  );
}

type Item = { to: string; label: string; badge?: ReactNode; match?: RegExp };

function NavGroup({ title, items, path }: { title?: string; items: Item[]; path: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      {title && (
        <span className="px-3 pb-1.5 font-mono text-[10px] uppercase tracking-[0.12em] text-[#8C8981]">{title}</span>
      )}
      {items.map((it) => {
        const active = it.match ? it.match.test(path) : path === it.to || path === `${it.to}/`;
        return (
          <Link
            key={it.to}
            to={it.to}
            className={cn(
              "flex min-h-9 items-center justify-between gap-2 rounded-md px-3 text-sm no-underline",
              active ? "bg-[#222220] font-medium text-[#ECEAE4] shadow-[inset_0_0_0_1px_#34332F]" : "text-[#A8A59D] hover:text-[#ECEAE4]",
            )}
          >
            <span>{it.label}</span>
            {it.badge}
          </Link>
        );
      })}
    </div>
  );
}

function Badge({ n, tone = "count" }: { n?: number; tone?: "hot" | "filled" | "count" }) {
  if (!n) return null;
  return (
    <span
      className={cn(
        "rounded-full px-[7px] py-0.5 font-mono text-[11px]",
        tone === "hot" ? "bg-[#FF4F1A] text-[#111111]" : tone === "filled" ? "bg-[#3A3935] text-[#ECEAE4]" : "px-0 text-[#8C8981]",
      )}
    >
      {n}
    </span>
  );
}

export const CITY: Record<string, string> = {
  nbg1: "Nuremberg",
  fsn1: "Falkenstein",
  hel1: "Helsinki",
  ash: "Ashburn",
  hil: "Hillsboro",
  sin: "Singapore",
};

export function ServerSidebar({ id }: { id: string }) {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const server = useServer(id).data;
  const { data: session } = authClient.useSession();
  const connected = !!server?.hub.connected;
  const proposals = useAgentQuery<{ pending: unknown[] }>(id, "proposals_list", {}, connected);
  const questions = useAgentQuery<{ open: unknown[]; pending: unknown[] }>(id, "questions_list", {}, connected);
  const openQuestions = Math.max(0, (questions.data?.open.length ?? 0) - (questions.data?.pending.length ?? 0));
  // same query keys as the Inbox cleanup and Sources pages, so these are shared, not extra calls
  const inbox = useAgentQuery<{ counts: Record<string, number> }>(id, "inbox_list", { tab: "suggested", account: "", q: "" }, connected);
  const sources = useAgentQuery<{
    gmail: { accounts: { connected: boolean }[] };
    slack: { accounts: { connected: boolean }[] };
    notion: { accounts: { connected: boolean }[] };
    github: { login?: string | null };
    telegram: { logged_in?: boolean };
  }>(id, "sources_status", {}, connected);
  const sd = sources.data;
  const nSources = sd
    ? [...sd.gmail.accounts, ...sd.slack.accounts, ...sd.notion.accounts].filter((a) => a.connected).length +
      (sd.github.login ? 1 : 0) +
      (sd.telegram.logged_in ? 1 : 0)
    : undefined;
  const openPlane = useOpenPlane(id);
  const s = server?.server;
  const base = `/servers/${id}`;
  const initials = (session?.user.name ?? "?")
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <nav aria-label="Fieldwork" className="flex min-h-full flex-col gap-[18px] border-r border-[#262523] bg-[#0C0C0B] px-3.5 pt-5 pb-[18px]">
      <Link to="/servers" className="flex items-center gap-2.5 px-1.5 text-[#ECEAE4] no-underline">
        <Logo />
        <span className="flex flex-col leading-[1.15]">
          <span className="font-mono text-[15px] font-bold tracking-tight">Fieldwork</span>
          <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-[#8C8981]">by Infinitum Labs</span>
        </span>
      </Link>

      <Link
        to="/servers"
        aria-label={`Switch server, current: ${s?.name ?? ""}`}
        className="flex items-center gap-2.5 rounded-lg bg-[#1A1A19] px-3 py-2.5 text-[#ECEAE4] no-underline shadow-[inset_0_0_0_1px_#2E2D2A]"
      >
        <span className={cn("size-2 shrink-0 rounded-full", connected ? "bg-[#5FBF7F]" : "bg-[#F0B65A]")} />
        <span className="flex min-w-0 flex-1 flex-col leading-[1.3]">
          <span className="truncate font-mono text-[13px] font-medium">{s?.name ?? "…"}</span>
          <span className="truncate text-xs text-[#8C8981]">
            {s?.provider === "hetzner" ? `Hetzner ${s.serverType ?? ""} · ${CITY[s.location ?? ""] ?? s.location ?? ""}` : "connected machine"}
          </span>
        </span>
        <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="#8C8981" strokeWidth="1.5" aria-hidden="true">
          <path d="M5 6l3-3 3 3M5 10l3 3 3-3" />
        </svg>
      </Link>

      <NavGroup
        path={path}
        items={[
          { to: base, label: "Today" },
          { to: `${base}/suggestions`, label: "Suggestions", badge: <Badge n={proposals.data?.pending.length} tone="hot" /> },
          { to: `${base}/questions`, label: "Questions", badge: <Badge n={openQuestions} tone="filled" /> },
          { to: `${base}/activity`, label: "Activity", match: new RegExp(`^${base}/(activity|runs|tasks)`) },
          { to: `${base}/inbox`, label: "Inbox cleanup", badge: <Badge n={inbox.data?.counts.suggested} /> },
        ]}
      />
      <NavGroup
        title="Knowledge"
        path={path}
        items={[
          { to: `${base}/people`, label: "People" },
          { to: `${base}/graph`, label: "Graph" },
          { to: `${base}/projects`, label: "Projects" },
        ]}
      />
      <NavGroup
        title="Connect"
        path={path}
        items={[
          { to: `${base}/sources`, label: "Sources", badge: <Badge n={nSources} /> },
          { to: `${base}/chat`, label: "Assistant" },
        ]}
      />
      <NavGroup
        title="Settings"
        path={path}
        items={[
          { to: `${base}/claude`, label: "AI & cost" },
          { to: `${base}/workflow`, label: "How I work" },
          { to: `${base}/server`, label: "Server" },
        ]}
      />

      <div className="grow" />

      {connected && (
        <button
          type="button"
          disabled={openPlane.isPending}
          onClick={() => openPlane.mutate(undefined)}
          className="flex min-h-9 items-center justify-between rounded-md px-3 text-sm text-[#A8A59D] shadow-[inset_0_0_0_1px_#2E2D2A] hover:text-[#ECEAE4]"
        >
          <span>Open Plane</span>
          <span aria-hidden="true">↗</span>
        </button>
      )}

      <div className="flex items-center gap-2.5 border-t border-[#262523] px-1.5 pt-3">
        <span className="grid size-8 shrink-0 place-items-center rounded-full bg-[#2A2927] font-mono text-xs font-bold">{initials}</span>
        <span className="flex min-w-0 flex-1 flex-col leading-[1.3]">
          <span className="truncate text-[13px] font-medium">{session?.user.name}</span>
          <span className="truncate text-xs text-[#8C8981]">{session?.user.email}</span>
        </span>
        <Link to="/tokens" aria-label="Account and cloud tokens" className="grid size-8 place-items-center rounded-md text-[#A8A59D] no-underline hover:text-[#ECEAE4]">
          <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
            <circle cx="8" cy="8" r="2.2" />
            <path d="M8 1.5v2M8 12.5v2M1.5 8h2M12.5 8h2M3.4 3.4l1.4 1.4M11.2 11.2l1.4 1.4M3.4 12.6l1.4-1.4M11.2 4.8l1.4-1.4" />
          </svg>
        </Link>
      </div>
    </nav>
  );
}

/** A page inside a server: header (title, subtitle, actions) and a content column, as on the canvas. */
export function ServerPage({
  title,
  subtitle,
  eyebrow,
  actions,
  back,
  children,
  wide,
  narrow,
  maxWidth,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  /** small mono label above the title, e.g. "Settings" */
  eyebrow?: ReactNode;
  actions?: ReactNode;
  back?: ReactNode;
  children: ReactNode;
  wide?: boolean;
  /** forms and settings: a 920px column */
  narrow?: boolean;
  /** any other content width in px (the canvas uses 1000 for Server, 1240 for Projects) */
  maxWidth?: number;
}) {
  return (
    <>
      <header className="flex flex-col gap-2.5 border-b border-[#262523] px-5 py-[22px] sm:px-10">
        {back}
        <div className={cn("flex flex-wrap justify-between gap-4", eyebrow ? "items-end" : "items-center")}>
          <div className="flex flex-col gap-1">
            {eyebrow && <span className="font-mono text-xs text-[#A8A59D]">{eyebrow}</span>}
            <h1 className="m-0 flex flex-wrap items-center gap-3 font-mono text-[26px] font-bold tracking-[-0.02em]">{title}</h1>
            {subtitle && <p className="m-0 max-w-[74ch] text-[13px] text-[#A8A59D]">{subtitle}</p>}
          </div>
          {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
        </div>
      </header>
      <div
        className={cn("box-border flex w-full flex-col gap-5 px-5 pt-6 pb-16 sm:px-10", wide ? "max-w-[1400px]" : narrow ? "max-w-[920px]" : "max-w-[1200px]")}
        style={maxWidth ? { maxWidth } : undefined}
      >
        {children}
      </div>
    </>
  );
}

/** Mono uppercase section label, as on the canvas. */
export function SectionTitle({ children, count }: { children: ReactNode; count?: number }) {
  return (
    <h2 className="m-0 font-mono text-[15px] font-bold">
      {children} {count !== undefined && <span className="text-[#A8A59D]">{count}</span>}
    </h2>
  );
}

/** A titled settings/form section on a card surface, as on the canvas. */
export function Panel({
  title,
  badge,
  actions,
  children,
  className,
  danger,
}: {
  title?: ReactNode;
  badge?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  className?: string;
  danger?: boolean;
}) {
  return (
    <section
      className={cn(
        "flex min-w-0 flex-col gap-3.5 rounded-md px-6 py-[22px]",
        danger ? "bg-[rgba(240,100,90,0.05)] shadow-[inset_0_0_0_1px_rgba(240,100,90,0.45)]" : "bg-[#1A1A19] shadow-[inset_0_0_0_1px_#2A2927]",
        className,
      )}
    >
      {(title || actions) && (
        <div className="flex flex-wrap items-center justify-between gap-2.5">
          <div className="flex flex-wrap items-center gap-2.5">
            {title && <h2 className={cn("m-0 font-mono text-base font-bold", danger && "text-[#F48A80]")}>{title}</h2>}
            {badge}
          </div>
          {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

/** Muted 13px explanation text. */
export function Note({ children, className }: { children: ReactNode; className?: string }) {
  return <p className={cn("m-0 text-[13px] text-[#A8A59D]", className)}>{children}</p>;
}

/** Card surface used for tables and panels. */
export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("rounded-md bg-[#1A1A19] shadow-[inset_0_0_0_1px_#2A2927]", className)}>{children}</div>;
}
